import { keepPreviousData, useQuery } from "@tanstack/vue-query";
import isDeepEqual from "fast-deep-equal";
import { computed, type MaybeRefOrGetter, toValue } from "vue";

import { debouncedRef } from "@/lib/composables/debounced-ref";
import { useStablePending } from "@/lib/composables/stable-pending";
import type { NewsletterEmailTemplate } from "@/types/email";

import { useNewsletterId, useXsrfHeaders } from "../utils";
import { fetchRender } from "./helpers";
import type {
  EmailDraftMedium,
  PreviewSource,
  PreviewSourceFactory,
} from "./types";

interface EmailDraftInput {
  id?: string;
  subject: string;
  body: string;
  template?: NewsletterEmailTemplate | null;
}

export const useEmailDraftPreview = <C extends EmailDraftMedium>(
  email: MaybeRefOrGetter<EmailDraftInput>,
  options: {
    context: C;
    subscriberId?: MaybeRefOrGetter<string | undefined>;
    enabled?: MaybeRefOrGetter<boolean>;
  }
): PreviewSource => {
  const newsletterId = useNewsletterId();
  const xsrfHeaders = useXsrfHeaders();

  const enabled = computed(() => toValue(options.enabled) ?? true);

  // Debounce content (subject/body/template) only. Selector changes —
  // subscriberId, contexts — flow through immediately.
  const debouncedContent = debouncedRef(
    computed<EmailDraftInput>((prev) => {
      const next = toValue(email);
      const subset: EmailDraftInput = {
        id: next.id,
        subject: next.subject,
        body: next.body,
        template: next.template,
      };
      if (prev !== undefined && isDeepEqual(prev, subset)) {
        return prev;
      }
      return subset;
    }),
    500,
    { enabled }
  );

  const queryParams = computed(() => ({
    ...debouncedContent.value,
    subscriberId: toValue(options.subscriberId),
    // not sure why useQuery() would complain otherwise
    context: options.context as EmailDraftMedium,
  }));

  const query = useQuery({
    queryKey: [newsletterId, "email-draft-preview", queryParams as any],
    enabled,
    gcTime: 10_000,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    queryFn({ signal }) {
      const params = queryParams.value;
      return fetchRender(
        {
          id: params.id,
          text: params.body,
          subject: params.subject,
          template: params.template ?? undefined,
          subscriber_id: params.subscriberId,
          context: params.context as C,
        },
        { signal, headers: xsrfHeaders.value }
      );
    },
  });

  const isStableFetching = useStablePending(query.isFetching);

  return {
    html: query.data,
    error: query.error,
    pending: computed(() => query.isLoading.value || isStableFetching.value),
  };
};

export const emailDraftPreviewSource = <C extends EmailDraftMedium>(
  context: C,
  email: MaybeRefOrGetter<EmailDraftInput>,
  options: { subscriberId?: MaybeRefOrGetter<string | undefined> } = {}
): PreviewSourceFactory<`email_draft:${C}`> => ({
  key: `email_draft:${context}`,
  create: ({ enabled }) => {
    return useEmailDraftPreview(email, {
      context,
      subscriberId: options.subscriberId,
      enabled,
    });
  },
});
